/**
 * Pending-admit — manual mode's confirm-before-write gate: one centred,
 * solid-backed card over the camera feed, its only write-triggering control
 * a 650ms press-and-hold (an early release snaps the fill to zero and fires
 * nothing) — so a stray thumb tap can never silently redeem a ticket.
 *
 * Reads `verdictState` and writes `commitPendingAdmit$`/`dismissRequested$`
 * directly — no props, no events — matching `verdict-splash-view`'s own
 * convention in this folder (schmancy's signals are the component API: the signal IS
 * the integration layer between co-located views). Mounted/unmounted by
 * `scan.view.ts`'s own `when()` gate on `verdict?.kind === "pending-admit"`.
 *
 * The card has two jobs, told apart by the scanned type's own `verifyAtDoor`.
 * An ordinary hold is the device's manual-mode brake: the operator confirms a
 * decode they are already watching. A verification hold is the catalog's own
 * refusal — this type is admitted by a person — and it can arrive in AUTO
 * mode, at a phone nobody is watching, so it announces itself (colour, tone,
 * haptic) and carries the type's `doorNote` as its whole instruction. Both
 * cards leave focus on the scan input: a barcode reader's trailing Enter
 * must never activate the separate admission control.
 *
 * This component owns the pending-admit confirm-before-write gate;
 * `ticket-sheet-view` covers only the read-only "see it again" pull-up.
 *
 * `opacity="solid"` on the one card (never a second nested surface) is
 * deliberate: this renders over a live camera feed of arbitrary brightness,
 * so the party-context line needs to sit ON that same surface at full
 * on-surface contrast to stay legible against a bright frame.
 */
import { sound } from "@mhmo91/schmancy";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { computed } from "@mhmo91/schmancy/state";
import { translate } from "@momo/i18n";
import { css, html } from "lit";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";
import { exhaustMap, interval, map, merge, Subject, takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { hapticsEnabled } from "../settings/settings.state.ts";
import {
	ticketTypeName,
	ticketTypesIndex,
} from "../ticket-types/ticket-types.state.ts";
import {
	commitPendingAdmit$,
	dismissRequested$,
	holderName,
	verdictState,
} from "./verdict.state.ts";

const HOLD_DURATION_MS = 650;
const HOLD_TICK_MS = 16;
const PRESSED_CLASS = "scale-[0.98]";

/** The pending ticket's own catalog row — the same lookup this card's header
 *  has always made for the type name, widened to the whole row because
 *  `verifyAtDoor` and `doorNote` live on it too. `null` while this event's
 *  catalog is still warming, and for a typeId it carries no entry for; neither
 *  reads as a verification hold, so a card the catalog cannot speak for is the
 *  ordinary one it has always been. */
const pendingAdmitType = computed(() => {
	const verdict = verdictState.value.verdict;
	return verdict?.kind === "pending-admit"
		? (ticketTypesIndex.value.byId.get(verdict.ticket.typeId) ?? null)
		: null;
});

@schmancyElement("pending-admit-view")
export class PendingAdmitView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				/* fixed, not absolute: a real scanner app IS the
				   viewport, mirroring verdict-splash-view's own convention. */
				position: fixed;
				inset: 0;
				z-index: 40;
				display: flex;
				align-items: center;
				justify-content: center;
				pointer-events: auto;
				animation: pending-admit-arrive 260ms ease-out both;
			}
			@keyframes pending-admit-arrive {
				0% {
					opacity: 0;
					transform: scale(0.96);
				}
				100% {
					opacity: 1;
					transform: scale(1);
				}
			}
			@media (prefers-reduced-motion: reduce) {
				:host {
					animation: none;
				}
			}
		`,
	];

	// Refs, not @state: the hold fires ~60 updates/sec and each one collapses
	// to one element-property write per element — mechanical-code's
	// a state cell earns its own re-render reserves @state for values a re-render
	// actually needs; a full Lit re-render on every tick would be pure waste.
	private readonly fillRef = createRef<HTMLDivElement>();
	private readonly buttonRef = createRef<HTMLButtonElement>();

	private readonly pointerDown$ = new Subject<void>();
	private readonly pointerUp$ = new Subject<void>();

	override connectedCallback() {
		super.connectedCallback();

		const verifyAtDoor = pendingAdmitType.get()?.verifyAtDoor === true;

		merge(
			this.pointerDown$.pipe(
				exhaustMap(() =>
					interval(HOLD_TICK_MS).pipe(
						map((i) =>
							Math.min(1, ((i + 1) * HOLD_TICK_MS) / HOLD_DURATION_MS),
						),
						takeUntil(this.pointerUp$),
					),
				),
			),
			// Early release resets to zero — idempotent after completion too,
			// since the completion branch below already zeroes it, and this
			// leg (subscribed at connect) always notifies before the interval
			// leg's own takeUntil(pointerUp$) unsubscribes it.
			this.pointerUp$.pipe(map(() => 0)),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe((pct) => {
				if (this.fillRef.value)
					this.fillRef.value.style.width = `${pct * 100}%`;
				this.buttonRef.value?.classList.toggle(
					PRESSED_CLASS,
					pct > 0 && pct < 1,
				);
				this.buttonRef.value?.setAttribute(
					"aria-pressed",
					String(pct > 0 && pct < 1),
				);
				if (pct >= 1) commitPendingAdmit$.next();
			});

		if (verifyAtDoor) {
			// A verification hold can arrive in AUTO mode, where every prior
			// scan has taught the operator that the phone needs nothing from
			// them and their eyes are on the queue. `worried` plus its 1:1
			// vibrate signature is splash.view.ts's own HOLD pair — the
			// vocabulary's one unresolved-until-you-act announcement, where
			// `frustrated` would read as refusing a ticket that is in fact
			// valid and `joyful`/`calm` are the admitting pair. play()
			// self-gates on schmancy's own mute setting (sound.service.ts's
			// own `play()`), the same gate splash relies on.
			sound.play("worried");
			if (hapticsEnabled.value && navigator.vibrate)
				navigator.vibrate([30, 40, 30]);
		}
	}

	override render() {
		const verdict = verdictState.value.verdict;
		if (verdict?.kind !== "pending-admit") return html``;
		const scannedType = pendingAdmitType.get();
		const verifyAtDoor = scannedType?.verifyAtDoor === true;
		const typeName =
			ticketTypeName(verdict.ticket.typeId, this.uiLanguage) ?? "";
		const holder = holderName.get();

		return html`
			<div class="absolute inset-0 bg-scrim/45 backdrop-blur-sm"></div>

			<schmancy-surface
				type="containerHigh"
				opacity="solid"
				elevated
				class="relative z-10 mx-6 flex w-full max-w-sm flex-col gap-4 overflow-hidden rounded-tl-2xl rounded-tr-2xl rounded-br-bezel p-5 shadow-2xl"
			>
				<div class="flex items-center gap-3">
					<schmancy-icon class=${this.classMap({ "text-4xl": true, "text-warning-default": verifyAtDoor })}
						>${verifyAtDoor ? "id_card" : "confirmation_number"}</schmancy-icon
					>
					<schmancy-typography type="headline" token="lg">${typeName}</schmancy-typography>
				</div>

				${when(
					verdict.party,
					(p) => html`
						<div class="flex items-center gap-2">
							<schmancy-icon>groups</schmancy-icon>
							<schmancy-typography type="body" token="md">
								${translate(this.uiLanguage, "scanner.ticketSheet.party", {
									total: p.total,
									alreadyIn: p.othersAlreadyIn,
									remaining: p.othersRemaining,
								})}
							</schmancy-typography>
						</div>
					`,
				)}

				<!-- Whose ticket this is, in the same plain fact row the party
				     count above uses and directly above the type's instruction
				     band below: for a verification hold the doorperson is being
				     asked to look at a person, and the name is the first thing
				     that look is against. A fact, never an instruction — it gets
				     no warning container, and the ticket admits or refuses
				     identically whether or not it ever arrives. -->
				${when(
					holder !== null,
					() => html`
						<div class="flex items-center gap-2">
							<schmancy-icon>redeem</schmancy-icon>
							<schmancy-typography type="title" token="md">
								${translate(this.uiLanguage, "scanner.verdict.holder", { name: holder ?? "" })}
							</schmancy-typography>
						</div>
					`,
				)}

				<!-- The type's own doorNote is the whole instruction — never a
				     second generic sentence restating it. Falsy-or, not
				     nullish-or: a note saved as an empty string is no
				     instruction, and the stand-in names the type, because
				     "Crew" alone tells a doorperson nothing to look at. -->
				${when(
					verifyAtDoor,
					() => html`
						<div class="flex items-start gap-2 rounded-2xl bg-warning-container px-3 py-2 text-warning-onContainer">
							<schmancy-icon>front_hand</schmancy-icon>
							<schmancy-typography type="title" token="sm">
								${
									scannedType?.doorNote ||
									translate(
										this.uiLanguage,
										"scanner.verdict.pendingAdmit.verify.checkFallback",
										{ typeName },
									)
								}
							</schmancy-typography>
						</div>
					`,
				)}

				<div class="flex flex-col gap-2 pt-1">
					<!-- Hand-rolled hold actuator — no schmancy primitive covers
					     press+hold+progress+confirm-on-completion as one control.
					     Native <button>, not schmancy-button: schmancy-button owns
					     tap semantics only, with no slot for a runtime progress
					     fill. -->
					<button
						${ref(this.buttonRef)}
						type="button"
						aria-label=${translate(
							this.uiLanguage,
							verifyAtDoor
								? "scanner.verdict.pendingAdmit.verify.holdAriaLabel"
								: "scanner.verdict.pendingAdmit.holdAriaLabel",
						)}
						aria-pressed="false"
						class=${this.classMap({
							"relative h-16 w-full select-none touch-manipulation overflow-hidden rounded-full transition-transform duration-150 focus-visible:ring-2": true,
							"bg-warning-container text-warning-onContainer focus-visible:ring-warning-default":
								verifyAtDoor,
							"bg-success-container text-success-onContainer focus-visible:ring-success-default":
								!verifyAtDoor,
						})}
						@pointerdown=${(e: PointerEvent) => {
							e.preventDefault();
							this.pointerDown$.next();
						}}
						@pointerup=${() => this.pointerUp$.next()}
						@pointercancel=${() => this.pointerUp$.next()}
						@pointerleave=${() => this.pointerUp$.next()}
						@click=${(e: MouseEvent) => {
							// Keyboard activation (Enter/Space on a focused native
							// button) fires `click` with detail 0 and no antecedent
							// pointerdown — the keyboard-equivalent confirm path. A
							// real pointer tap's click always carries detail >= 1 and
							// is already gated by the hold pipeline above, so this
							// never lets a stray tap through.
							if (e.detail !== 0) return;
							if (this.fillRef.value) this.fillRef.value.style.width = "0%";
							commitPendingAdmit$.next();
						}}
					>
						<div
							${ref(this.fillRef)}
							class=${this.classMap({
								"absolute inset-y-0 left-0 w-0": true,
								"bg-warning-default": verifyAtDoor,
								"bg-success-default": !verifyAtDoor,
							})}
						></div>
						<div class="relative flex h-full items-center justify-center gap-2">
							<schmancy-icon>${verifyAtDoor ? "how_to_reg" : "check_circle"}</schmancy-icon>
							<schmancy-typography type="title" token="md">
								${translate(
									this.uiLanguage,
									verifyAtDoor
										? "scanner.verdict.pendingAdmit.verify.hold"
										: "scanner.verdict.pendingAdmit.hold",
								)}
							</schmancy-typography>
						</div>
					</button>
					<schmancy-button variant="text" width="full" @click=${() => dismissRequested$.next()}>
						${translate(this.uiLanguage, "scanner.ticketSheet.reject")}
					</schmancy-button>
				</div>
			</schmancy-surface>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"pending-admit-view": PendingAdmitView;
	}
}
